import { Controller } from '@hotwired/stimulus';

/** T-vocal / D4 — LE TIMEOUT DÉGRADE, IL N'ÉTEINT PLUS.
 *
 *  90 s reste le seuil « ce tour sort de la normale mesurée » (un tour vocal
 *  corrigé tient en 11-23 s : 2,5 s de debounce + ≈3,5 s de Voxtral + 1 à 2
 *  rounds LLM à 5,2 s p50 / 8,3 s p90, sur 52 rounds mesurés). Ce qui change,
 *  c'est ce qu'on en fait : l'indicateur passait de « visible » à « éteint »,
 *  et sur l'incident du 2026-08-22 il a laissé ≈70 s de silence TOTAL avant
 *  l'excuse. Il change désormais de LIBELLÉ et reste visible. */
const TYPING_SLOW_AFTER_MS = 90000;

/** L'extinction, elle, recule à 6 min 30 : ce délai est armé PAR
 *  `#degradeTyping`, donc APRÈS les 90 s de `TYPING_SLOW_AFTER_MS` — 90 s
 *  + 5 min, et non 5 min depuis l'envoi. C'est au-dessus du pire tour jamais
 *  mesuré (2 min 42 s, incident du 2026-08-22 : trois tentatives de retry_on
 *  avec backoff) et bien en deçà de la lane du job
 *  (AGENT_LANE_DURATION_MINUTES, 15 min). Au-delà, le worker est réellement
 *  mort et continuer à afficher
 *  « ça travaille » serait un mensonge — c'est le seul cas où l'extinction est
 *  la bonne réponse, tous les chemins terminaux du job poussant une bulle. */
const TYPING_HARD_STOP_MS = 300000;

/** Conteneur préféré de MediaRecorder. `AUDIO_CONTENT_TYPES` du serveur est
 *  l'allowlist de `Agents::Transcription` (audio/ogg + audio/webm) ; opus dans
 *  du webm est le seul couple que Chrome et Firefox produisent tous les deux.
 *  Repli sur le défaut du navigateur si le couple n'est pas supporté (Safari :
 *  mp4/aac — le serveur le REFUSERA avec son message FR, ce qui est le
 *  comportement voulu, pas un silence). */
const PREFERRED_MIME = 'audio/webm;codecs=opus';

/** Durcissement console dev — la demande de permission micro peut rester
 *  PENDANTE indéfiniment (ni succès ni rejet) quand le NAVIGATEUR lui-même
 *  n'a pas l'autorisation micro au niveau système macOS : `catch` ne part
 *  jamais, aucun bandeau, un bouton qui « ne fait rien » (cas réel, Arc/macOS).
 *  8 s est un compromis mesuré : assez long pour ne jamais parler par-dessus
 *  une invite d'autorisation légitimement lente (la QA a observé plusieurs
 *  secondes d'attente normale avant la boîte de dialogue du navigateur), assez
 *  court pour ne pas laisser l'utilisateur devant un bouton muet. Ce délai
 *  INFORME sans jamais ANNULER : si l'autorisation arrive ensuite, l'enregis-
 *  trement démarre normalement et le bandeau se referme (§2/§3 du ticket). */
const MIC_PERMISSION_TIMEOUT_MS = 8000;

/**
 * Console agent (dev only) — compositeur, retour d'attente et enregistreur vocal.
 *
 * Monté sur la CARTE de chat (le <main>), pas sur le formulaire : les targets
 * `messages` et `typing` sont des frères du compositeur et Stimulus ne résout
 * ses targets que dans son propre sous-arbre.
 *
 * Targets:
 *   • form              — le <form> du compositeur (Entrée envoie).
 *   • input             — le textarea.
 *   • inputWrapper      — l'emballage du textarea. C'est LUI qui reçoit `hidden`
 *                         pendant un enregistrement (D3 : `block` sur le textarea
 *                         et `hidden` sur le même nœud se disputeraient la
 *                         cascade v4). Son contenu n'est jamais touché.
 *   • uuid              — hidden, clé de dédup client du canal Dev.
 *   • buttonId          — hidden, `button_id` du choix de bulle cliqué (T5,
 *                         `chooseReply`). Rempli PUIS VIDÉ juste après
 *                         `requestSubmit()` (Turbo a déjà capturé la FormData de
 *                         façon SYNCHRONE dans ce même appel — le listener
 *                         Stimulus posé sur le `<form>` passe en phase CIBLE,
 *                         celui de Turbo en phase BULLE au niveau document, même
 *                         garantie que `prepare()` ci-dessous) : sans ce
 *                         nettoyage, une frappe suivante repartirait avec un
 *                         `button_id` FANTÔME.
 *   • messages          — le fil (#agent-console-messages), cible des `append` Turbo.
 *   • typing            — l'indicateur « en train d'écrire », HORS du fil, masqué par
 *                         la classe `hidden` seule.
 *   • mediaAlert        — la boîte d'erreur média, rendue INCONDITIONNELLEMENT et
 *                         masquée par la CLASSE `hidden` (jamais l'attribut).
 *   • mediaAlertMessage — le <span> dont on écrit le `textContent` (JAMAIS
 *                         `innerHTML` : le message vient du serveur, chemin XSS #591).
 *   • photoButton       — le bouton trombone, désactivé pendant un enregistrement.
 *   • micButton         — le bouton micro (démarre / arrête).
 *   • recordingBar      — la barre d'enregistrement, racine `hidden` SEULE.
 *   • recordingDot      — la pastille pulsée, éteinte à l'arrêt automatique.
 *   • recordingLabel    — le texte DANS le `role="status"`, écrit APRÈS le dévoilement.
 *   • recordingTimer    — le chrono `tabular-nums`, `aria-hidden`.
 *   • cancelWrapper     — l'emballage du bouton « Annuler » (même piège D3).
 *   • audioInput        — l'`<input type=file>` caché où le Blob est posé via
 *                         `DataTransfer`. Il porte `name="media[]"` : l'envoi
 *                         repart par le chemin multipart DÉJÀ en place.
 *
 * Values:
 *   • maxSeconds  — plafond de durée, arrêt AUTOMATIQUE (décision (6), 300 s).
 *                   L'arrêt ne déclenche JAMAIS l'envoi.
 *   • warnSeconds — bascule du timer en warning (270 s = 4:30).
 *
 * Outlet:
 *   • photo-picker — la bande de miniatures du compositeur. Deux appels : `browse()`
 *     (le bouton « Joindre une photo ou un PDF » est un FRÈRE du picker, donc hors
 *     de sa portée Stimulus) et `clear()` après un envoi réussi — le POST répond
 *     `no_content`, donc rien ne viderait la pile et les mêmes pièces repartiraient
 *     au message suivant.
 *
 * Actions (déclarées sur les nœuds émetteurs, qui remontent au contrôleur
 * ancêtre) : submit->prepare (pose l'uuid, OU suspend la soumission le temps
 * d'arrêter l'enregistreur), turbo:submit-end->afterSubmit (RÉ-ACTIVE la
 * rangée quick-actions en tout premier — succès ET échec —, vide, re-focus,
 * arme l'indicateur), keydown->keydown sur le textarea (Entrée envoie,
 * Maj+Entrée = retour ligne), click->browsePhoto sur le bouton photo,
 * click->toggleRecording sur le micro, click->cancelRecording sur « Annuler »,
 * click->quickAction sur chaque bouton de la rangée quick-actions (T9, écrit
 * `event.params.text` dans le compositeur et soumet SON formulaire ; fix
 * round 1 : R8/#693, même désactivation SYNCHRONE avant `requestSubmit()`
 * que `chooseReply`, mais RÉ-ACTIVÉE à `afterSubmit` — une commande rapide
 * reste réutilisable après le tour, contrairement à un bouton de réponse),
 * click->chooseReply sur chaque bouton d'une bulle de réponses proposées (T5,
 * `dev/agent/console/_choices.html.erb`, contrat pinné par T2 : lit
 * `data-button-id`/`data-button-label`/`data-button-group` sur le bouton
 * cliqué, désactive LA PAIRE de SON offre de façon SYNCHRONE avant tout
 * `requestSubmit()` — R8/#693, anti double-clic, DÉFINITIVEMENT (pas de
 * ré-activation) — puis remplit le compositeur et soumet SON formulaire).
 * Le grain de désactivation est la PAIRE, pas la bulle : voir
 * `#disableChoiceGroup`.
 *
 * L'indicateur s'éteint sur l'ARRIVÉE d'une bulle ASSISTANT dans le fil, vue
 * par un MutationObserver posé en connect() et retiré en disconnect(). Le
 * discriminant `data-agent-message-role` est load-bearing DES DEUX CÔTÉS : la
 * bulle optimiste de l'utilisateur est poussée par le même aller-retour que
 * l'envoi et éteindrait sinon l'indicateur dans la milliseconde qui suit son
 * affichage ; et un bandeau `system` (accusé de transcription, ouverture de
 * séance) annonce un fait SANS terminer l'attente. Voir `#hideTypingOnReply`.
 *
 * ⚠️ AUCUNE classe ni couleur n'est écrite en dur ici (D14) : les jeux de classes
 * d'état descendent de l'ERB en data-attrs (`data-recording-class`,
 * `data-disabled-class`, `data-warn-class`, …), comme la copie FR (D15). Le JS
 * n'écrit que du `textContent` — jamais d'`innerHTML`, jamais de `<svg>`.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * QA MANUELLE — `yarn test:js` couvre les courses déterministes de permission
 * micro, dont le minuteur actif du second essai. Le contrat COMPLET de
 * MediaRecorder (périphérique réel, voyant navigateur et navigation Turbo)
 * reste à rejouer au navigateur, sur `/dev/agent/console`, SESSION OUVERTE
 * (hors session le serveur refuse tout octet en 422 — c'est SPEC-SILENT 16,
 * pas un bug de l'enregistreur) :
 *
 *   1. ENREGISTRER PUIS ENVOYER — taper du texte, cliquer le micro : la barre
 *      rouge REMPLACE le textarea (le texte reste saisi, invisible), le bouton
 *      photo grise, le timer court. Parler ~3 s, cliquer « Envoyer ».
 *      Attendu : une bulle user apparaît, le textarea revient VIDE (son texte
 *      est parti AVEC le vocal), l'indicateur « en train d'écrire » s'arme, et
 *      `AgentInboundEvent.last.payload['kind'] == 'audio'` avec un blob
 *      `vocal.webm` non vide.
 *
 *   2. ANNULER — micro, parler ~3 s, cliquer « Annuler ».
 *      Attendu : la rangée redevient normale, LE TEXTE DÉJÀ TAPÉ EST INTACT,
 *      le bouton photo se réactive, AUCUN POST n'est parti (0 nouvel
 *      `AgentInboundEvent`), et le voyant micro de l'onglet s'éteint (les
 *      pistes du flux sont bien relâchées).
 *
 *   3. PERMISSION REFUSÉE — dans les réglages du site, mettre « Micro » sur
 *      « Bloquer », recharger, cliquer le micro.
 *      Attendu : l'alerte inline s'affiche au-dessus de la rangée avec la
 *      phrase « Micro indisponible : autorisez l'accès… », le bouton micro
 *      RESTE ACTIF (on peut retenter après avoir autorisé), aucune barre ne
 *      s'ouvre. Variante décision (5) : ouvrir la console via l'IP LAN en http
 *      (`navigator.mediaDevices` est alors `undefined`) ⇒ l'alerte s'affiche
 *      DÈS LE CHARGEMENT et le micro est en grisé marqué, aria-label
 *      « Micro indisponible sur cette connexion ».
 *
 *   4. PLAFOND 5 MIN — micro, laisser tourner. Attendu : à 4:30 le timer passe
 *      en warning et affiche « 4:31 / 5:00 » ; à 5:00 l'enregistrement S'ARRÊTE
 *      SEUL, la pastille cesse de pulser et s'éteint, le libellé passe à
 *      « Enregistrement prêt à envoyer », le timer se fige — et RIEN N'EST
 *      ENVOYÉ. « Envoyer » expédie alors le vocal, « Annuler » le jette.
 *      (Raccourci de QA : passer `data-agent-console-max-seconds-value` à 10 et
 *      `warn-seconds` à 5 dans l'inspecteur avant de cliquer le micro.)
 *
 *   5. PERMISSION QUI NE RÉPOND JAMAIS (macOS : navigateur non autorisé au
 *      niveau système, cas réel rencontré sous Arc) — retirer l'autorisation
 *      micro du NAVIGATEUR dans Réglages Système > Confidentialité > Micro,
 *      SANS toucher aux réglages du site, cliquer le micro et attendre.
 *      Attendu : après `MIC_PERMISSION_TIMEOUT_MS` (8 s) l'alerte inline
 *      s'affiche avec la phrase de timeout ET le bouton micro REDEVIENT
 *      CLIQUABLE (la demande, elle, reste pendante en arrière-plan —
 *      décision (1)/(2) : elle n'est jamais annulée ; sans autorisation
 *      système elle ne se résoudra jamais, donc un bouton resté mort
 *      laisserait la consigne « puis réessayez » inexécutable). Accorder
 *      ensuite l'autorisation système PUIS RECLIQUER LE BOUTON JERICO (le
 *      geste attendu, mesuré en QA impersonée) : Attendu : le clic mine une
 *      demande neuve, l'enregistrement démarre normalement dès qu'elle se
 *      résout et le bandeau se referme ; quand la toute première demande
 *      (périmée) se résout à son tour, RIEN ne se voit — son flux est coupé
 *      en silence, elle ne redémarre ni n'interrompt rien. Variante :
 *      naviguer hors de la console (Turbo) PENDANT l'attente ⇒ Attendu : à la
 *      résolution tardive, le voyant micro de l'onglet s'éteint quand même
 *      (piste coupée), aucune erreur console.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * @example
 *   <main data-controller="agent-console"
 *         data-agent-console-max-seconds-value="300"
 *         data-agent-console-warn-seconds-value="270">
 *     <div id="agent-console-messages" data-agent-console-target="messages"></div>
 *     <div id="agent-typing-indicator" class="hidden" data-agent-console-target="typing"
 *          role="status" aria-live="polite"
 *          data-typing-label="…" data-typing-slow-label="…">
 *       <span data-agent-console-target="typingLabel">…</span>
 *     </div>
 *     <form data-agent-console-target="form"
 *           data-action="submit->agent-console#prepare
 *                        turbo:submit-end->agent-console#afterSubmit">…</form>
 *   </main>
 */
export default class extends Controller {
  static targets = [
    'form',
    'input',
    'inputWrapper',
    'uuid',
    'buttonId',
    'messages',
    'typing',
    'typingLabel',
    'mediaAlert',
    'mediaAlertMessage',
    'photoButton',
    'micButton',
    'recordingBar',
    'recordingDot',
    'recordingLabel',
    'recordingTimer',
    'cancelWrapper',
    'audioInput',
  ];
  static outlets = ['photo-picker'];
  static values = {
    maxSeconds: { type: Number, default: 300 },
    warnSeconds: { type: Number, default: 270 },
  };

  connect() {
    this.#resetRecorderState();
    this.#markMicUnavailableIfNeeded();

    if (!this.hasMessagesTarget) return;

    this.replyObserver = new MutationObserver((mutations) => this.#hideTypingOnReply(mutations));
    this.replyObserver.observe(this.messagesTarget, { childList: true });
  }

  // Symétrie stricte : le MutationObserver, le tick du timer, le minuteur de
  // permission micro ET les pistes du flux micro. Une piste laissée ouverte
  // garde le voyant d'enregistrement allumé dans l'onglet APRÈS une
  // navigation Turbo — Turbo détruit puis reconnecte les contrôleurs, donc la
  // fuite s'accumulerait.
  disconnect() {
    this.replyObserver?.disconnect();
    this.replyObserver = null;
    this.#clearTypingTimeout();
    this.#clearTick();
    this.#clearMicPermissionTimeout();
    // Invalide toute demande de permission micro encore pendante : si
    // `getUserMedia` se résout APRÈS ce démontage, `#startRecording` la
    // reconnaîtra comme périmée et coupera ses pistes sans toucher au DOM.
    this.micRequestToken = null;
    this.#stopRecorderNow();
    this.#releaseStream();
  }

  // Le Blob de MediaRecorder n'existe qu'au `stop`, qui est ASYNCHRONE : on ne
  // peut pas le poser dans l'input depuis un gestionnaire de `submit`. On
  // suspend donc CETTE soumission et on la relance depuis `onstop`, une fois le
  // File posé. Turbo écoute `submit` en phase bulle AU NIVEAU DOCUMENT et
  // respecte `defaultPrevented` : un listener Stimulus posé sur le <form>
  // lui-même passe avant, donc le `preventDefault` mord réellement.
  prepare(event) {
    if (this.#isRecording()) {
      event.preventDefault();
      this.#stopRecording({ submit: true });
      return;
    }

    this.uuidTarget.value = crypto.randomUUID();
  }

  keydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.formTarget.requestSubmit();
    }
  }

  // Rangée quick-actions (T9) : le clic ÉCRIT le mot-clé dans le compositeur
  // et soumet LE MÊME formulaire — jamais un fetch parallèle. `event.params.text`
  // est le contrat Stimulus « action params », posé en `data-agent-console-text-param`
  // sur chaque bouton (dev/agent/console/show.html.erb) ; ces textes matchent
  // EXACTEMENT une entrée de `Agents::KeywordInterceptor::KEYWORDS` côté serveur.
  //
  // R8/#693, fix round 1 (revue T5+T9) : même discipline anti-double-clic que
  // `chooseReply` — désactivation SYNCHRONE, DANS ce handler, AVANT tout
  // `requestSubmit()`. Différence VOULUE avec `chooseReply` : une commande
  // rapide reste un geste RÉUTILISABLE après le tour (« Stop » peut resservir
  // à la réunion suivante), donc la rangée est RÉ-ACTIVÉE dans `afterSubmit`
  // (`turbo:submit-end`, sur SUCCÈS **et** ÉCHEC — un 422 média ne doit pas
  // laisser la rangée morte), contrairement au groupe de réponses de
  // `chooseReply` qui reste désactivé pour de bon (une bulle passée ne
  // reprend jamais de réponse).
  quickAction(event) {
    if (!this.hasInputTarget || !this.hasFormTarget) return;

    const button = event.currentTarget;
    if (button.disabled) return;

    this.#disableQuickActions(button);

    this.inputTarget.value = event.params.text;
    this.formTarget.requestSubmit();
  }

  // Clic sur un bouton de bulle de réponses proposées (T5,
  // `dev/agent/console/_choices.html.erb`, contrat cross-layer PINNÉ par T2) :
  // `data-button-id`/`data-button-label` sont posés sur le bouton CLIQUÉ
  // (`event.currentTarget`, jamais `event.target` : un futur enfant du bouton —
  // icône, span — ferait remonter le mauvais nœud). `dataset.buttonLabel`
  // arrive DÉCODÉ (les entités HTML sont résolues par le navigateur) : usage en
  // `value`/`textContent` UNIQUEMENT, jamais réinjecté en HTML.
  chooseReply(event) {
    const button = event.currentTarget;
    const { buttonId, buttonLabel } = button.dataset;
    if (!buttonId || !buttonLabel || !this.hasInputTarget || !this.hasFormTarget) return;

    // R8/#693 — RÈGLE ANTI-DOUBLE-CLIC : le groupe est désactivé de façon
    // SYNCHRONE, DANS CE HANDLER, AVANT tout `requestSubmit()`. Aucune fenêtre
    // ne doit exister où un second clic pourrait encore partir.
    this.#disableChoiceGroup(button);
    // Désactiver le bouton cliqué l'ÉJECTE IMMÉDIATEMENT du tab order (la
    // spec HTML fait alors retomber le focus sur <body>) : on redirige le
    // focus vers le champ de saisie tout de suite, sans attendre l'aller-
    // retour réseau (`afterSubmit` le refait après coup, sans mal).
    this.inputTarget.focus();

    this.inputTarget.value = buttonLabel;
    if (this.hasButtonIdTarget) this.buttonIdTarget.value = buttonId;

    this.formTarget.requestSubmit();

    // Vidé tout de suite après la soumission : voir la note du target
    // `buttonId` en tête de fichier — Turbo a déjà capturé la FormData de
    // façon synchrone à l'intérieur de l'appel ci-dessus.
    if (this.hasButtonIdTarget) this.buttonIdTarget.value = '';
  }

  /**
   * Désactive LA PAIRE de boutons de l'offre cliquée — jamais toute la bulle —
   * et pose, JUSTE APRÈS le dernier bouton de cette paire, un texte d'état
   * lisible par un lecteur d'écran. Jamais un retrait de nœuds : le fil est une
   * ARCHIVE.
   *
   * POURQUOI LA PAIRE ET PAS LE GROUPE (F1). `Outbound::Collector#offer`
   * CONCATÈNE les choix de tous les tours d'un même envoi, et le présentateur
   * n'émet qu'UNE bulle : deux offres de kinds différents partagent donc un
   * seul `role="group"` (4 choix > MAX_REPLY_BUTTONS ⇒ branche `send_list`).
   * L'unité de consommation côté serveur est la LIGNE `agent_pending_actions`,
   * pas la bulle — désactiver le groupe entier tuait l'offre sœur, encore
   * `pending` en base et donc parfaitement cliquable. `data-button-group` NOMME
   * cette paire (== le token de l'action), posé par `_choices.html.erb` depuis
   * la clé `'group'` d'`OfferChoices.for`. La paire n'est JAMAIS dérivée ici du
   * préfixe `refuse:` : ce préfixe a un propriétaire unique côté serveur
   * (`pending_actions/offer_choices.rb`) et une copie JS dériverait en silence.
   *
   * REPLI [R-3] — BULLE MIXTE. Un bouton SANS `data-button-group` (ligne de
   * désambiguïsation T6/T7, lobby, refus hors paire) répond à la QUESTION, pas
   * à une offre : on désactive alors tous les boutons SANS group du même
   * `role="group"`, et EUX SEULS — les paires groupées de la même bulle restent
   * vivantes. Le vieux comportement « tout le groupe » ne subsiste que par
   * coïncidence, quand AUCUN bouton de la bulle ne porte de group (cas pur des
   * listes de désambiguïsation), ce qui est exactement la sémantique voulue là.
   *
   * CE N'EST QU'UNE AFFORDANCE UX LOCALE, jamais un mécanisme de correction :
   * sur WhatsApp réel rien ne se consomme côté client, un vieux reply button
   * reste tapable pour toujours et c'est le SERVEUR qui absorbe les taps
   * périmés (`resolve_pending_action` → « Cette proposition n'existe plus. »,
   * claim idempotent). D'où deux conséquences assumées : (S2) le statut
   * « Réponse envoyée. » n'est jamais retiré même si le claim est refusé — la
   * paire est morte quel que soit le verdict, et la bulle serveur porte la
   * vérité ; (S4) cliquer la paire B pendant que le POST de la paire A est en
   * vol reste PERMIS — chaque clic mint son `external_id`, le claim est
   * déterministe et idempotent, on ne construit pas de file d'attente pour une
   * console dev.
   *
   * Le texte est lu depuis `data-answered-text` si un rendu futur en ajoute un,
   * sinon composé ici en `textContent` — JAMAIS `innerHTML`. Les CLASSES, elles,
   * viennent TOUJOURS de `data-answered-text-class`, posé par `_choices.html.erb`
   * sur le `role="group"` (D14 : aucun jeton Tailwind ni couleur écrit en dur
   * ici) — même patron que `#applyTokens` plus bas, mais la SOURCE du data-attr
   * (le groupe) diffère de la CIBLE du `classList.add` (le `<span>` fraîchement
   * créé), donc pas de réutilisation directe de `#applyTokens`.
   *
   * Appelé de façon SYNCHRONE depuis `chooseReply`, AVANT `requestSubmit()`
   * (R8/#693) : ne jamais déplacer cet appel après la soumission.
   */
  #disableChoiceGroup(clickedButton) {
    const group = clickedButton.closest('[role="group"]');
    if (!group) return;

    const pair = clickedButton.dataset.buttonGroup;
    // `:not([data-button-group])` distingue « pas de paire » de « paire vide » —
    // c'est pour ça que l'ERB n'émet l'attribut QUE si la clé est présente.
    const selector = pair
      ? `button[data-button-group="${CSS.escape(pair)}"]`
      : 'button:not([data-button-group])';
    const buttons = Array.from(group.querySelectorAll(selector));
    if (buttons.length === 0) return;

    buttons.forEach((choiceButton) => {
      choiceButton.disabled = true;
    });

    const status = document.createElement('span');
    status.setAttribute('role', 'status');
    status.textContent = group.dataset.answeredText || 'Réponse envoyée.';
    const tokens = (group.dataset.answeredTextClass || '').split(' ').filter(Boolean);
    if (tokens.length > 0) status.classList.add(...tokens);
    // APRÈS le dernier bouton de la paire, pas en fin de groupe (S3) : deux
    // paires ⇒ deux `role="status"` localisés, chacun collé à ses boutons.
    buttons[buttons.length - 1].insertAdjacentElement('afterend', status);
  }

  // Désactive TOUS les boutons de la rangée quick-actions (fix round 1,
  // symétrique de `#disableChoiceGroup` mais TEMPORAIRE — voir
  // `#reenableQuickActions`). Cherché par attribut plutôt que par target
  // Stimulus : la rangée n'a pas besoin d'être une target tant qu'aucune
  // autre méthode n'a besoin de la référencer.
  #disableQuickActions(clickedButton) {
    const row = clickedButton.closest('[data-agent-console-target="quickActions"]');
    if (!row) return;

    row.querySelectorAll('button').forEach((button) => {
      button.disabled = true;
    });
  }

  // Ré-active la rangée quick-actions après le tour (succès OU échec) : une
  // commande rapide reste RÉUTILISABLE, contrairement aux boutons de réponse
  // d'une bulle passée (`#disableChoiceGroup`), qui eux restent morts.
  #reenableQuickActions() {
    const row = this.element.querySelector('[data-agent-console-target="quickActions"]');
    if (!row) return;

    row.querySelectorAll('button').forEach((button) => {
      button.disabled = false;
    });
  }

  // Le bouton photo est un FRÈRE du picker : son `data-action` ne peut remonter
  // qu'à ce contrôleur-ci, qui relaie par l'outlet.
  browsePhoto() {
    if (this.hasPhotoPickerOutlet) this.photoPickerOutlet.browse();
  }

  // ---------------------------------------------------------------- vocal --

  // Démarre, ou ARRÊTE en gardant l'enregistrement « prêt à envoyer » (jamais
  // d'envoi implicite — décision (6) : seul « Envoyer » expédie).
  toggleRecording() {
    if (this.#isRecording()) {
      this.#stopRecording({ submit: false });
      return;
    }

    this.#startRecording();
  }

  // Décision (2) : le geste est RÉVERSIBLE et sans effet serveur — aucune
  // confirmation modale (D4 : cette page n'héberge pas le composant global).
  // Le texte du textarea n'est jamais touché, il réapparaît tel quel.
  cancelRecording() {
    this.cancelled = true;
    this.#clearTick();

    if (this.#isRecording()) {
      // `onstop` verra `cancelled` et jettera les chunks.
      this.recorder.stop();
      return;
    }

    this.#discardRecording();
    if (this.hasInputTarget) this.inputTarget.focus();
  }

  async #startRecording() {
    if (!this.hasMicButtonTarget || this.micButtonTarget.disabled) return;

    // Jeton de la demande EN COURS : la seule façon de reconnaître, à la
    // résolution de la promesse, qu'elle est toujours celle qu'on attend.
    // Deux voies l'invalident : `disconnect()` (navigation, démontage) et un
    // SECOND appel de `#startRecording` — réel et ATTENDU (fix round 1,
    // mesuré en QA impersonée : le premier clic reste muet plusieurs
    // secondes, c'est le SECOND qui débloque). Le bouton redevient cliquable
    // dès l'expiration du minuteur (ligne suivante), précisément pour ouvrir
    // cette voie ; le jeton est ce qui rend le second appel SÛR : le second
    // clic en mine un nouveau, qui périme le premier, et la résolution
    // tardive de la demande périmée (succès OU rejet) est reconnue par
    // comparaison et n'entre JAMAIS en conflit avec la nouvelle.
    const requestToken = Symbol('mic-permission-request');
    this.micRequestToken = requestToken;
    this.micButtonTarget.disabled = true;

    // Minuteur qui INFORME sans ANNULER (§1/§2 du ticket) : à l'expiration,
    // si la promesse est TOUJOURS pendante ET que cette demande est toujours
    // la dernière attendue, on révèle le bandeau ET RÉACTIVE le bouton — la
    // demande de permission, elle, continue de vivre exactement comme avant,
    // en arrière-plan. Réactiver le bouton est ce qui rend « puis réessayez »
    // exécutable : sans autorisation système, cette promesse-là NE SE
    // RÉSOUDRA JAMAIS, et laisser le bouton mort mettrait la seule sortie
    // hors de portée (le rechargement de page). Un second clic mine un
    // nouveau jeton (ci-dessus) : la demande périmée, quoi qu'il arrive
    // ensuite, sera reconnue et neutralisée sans toucher au DOM ni à
    // l'enregistrement du second essai.
    this.micPermissionTimeout = setTimeout(() => {
      if (this.micRequestToken === requestToken) {
        this.#revealMediaError(this.#alertCopy('timeoutMessage'));
        this.micButtonTarget.disabled = false;
      }
    }, MIC_PERMISSION_TIMEOUT_MS);

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      // Résolution tardive d'une demande périmée — un second clic après
      // expiration en a miné une nouvelle, OU le contrôleur a été démonté
      // entre-temps : dans les deux cas, ne touche à AUCUNE target (l'élément
      // a pu disparaître) et laisse la demande courante suivre son cours.
      if (this.micRequestToken !== requestToken) return;

      this.#clearMicPermissionTimeout();

      // NotAllowedError (refus), NotFoundError (aucun micro), SecurityError…
      // Décision (4) : le bouton micro RESTE ACTIF, la re-tentative est le
      // geste attendu une fois l'autorisation accordée.
      this.micButtonTarget.disabled = false;
      this.#revealMediaError(this.#alertCopy('deniedMessage'));
      return;
    }

    // §4 du ticket : la demande n'est plus la DERNIÈRE attendue — soit un
    // second clic après timeout en a miné une nouvelle (fix round 1), soit le
    // contrôleur a été déconnecté pendant l'attente (navigation, démontage
    // Turbo). Le flux arrive mais personne ne l'attend plus : un micro chaud
    // sans interface est pire que le défaut qu'on corrige. On coupe les
    // pistes et on sort SANS toucher au DOM (l'élément a pu disparaître).
    if (this.micRequestToken !== requestToken) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    this.#clearMicPermissionTimeout();

    this.micButtonTarget.disabled = false;
    this.#hideMediaError();
    this.stream = stream;
    this.chunks = [];
    this.cancelled = false;
    this.submitAfterStop = false;

    this.recorder = new MediaRecorder(stream, this.#recorderOptions());
    this.recorderMimeType = this.recorder.mimeType || PREFERRED_MIME;
    this.recorder.addEventListener('dataavailable', (event) => {
      if (event.data && event.data.size > 0) this.chunks.push(event.data);
    });
    this.recorder.addEventListener('stop', () => this.#onRecorderStop());
    this.recorder.start();

    this.#enterRecordingUi();
  }

  #recorderOptions() {
    const supported = MediaRecorder.isTypeSupported?.(PREFERRED_MIME);
    return supported ? { mimeType: PREFERRED_MIME } : {};
  }

  #stopRecording({ submit }) {
    this.submitAfterStop = submit;
    this.#clearTick();

    if (this.#isRecording()) {
      this.recorder.stop();
      return;
    }

    this.#onRecorderStop();
  }

  // Point de convergence des TROIS arrêts : clic micro, plafond 5 min, et
  // « Envoyer » pendant l'enregistrement.
  #onRecorderStop() {
    this.#releaseStream();

    if (this.cancelled) {
      this.#discardRecording();
      return;
    }

    const blob = new Blob(this.chunks, { type: this.recorderMimeType });
    const submit = this.submitAfterStop;
    this.submitAfterStop = false;

    if (blob.size === 0) {
      // Rien n'a été capturé (arrêt quasi immédiat, piste muette matériellement
      // absente) : on ne pose AUCUN fichier — un fichier 0 octet serait de
      // toute façon sauté par le serveur, mais il ferait basculer le `kind` de
      // l'entrant sur `audio` et masquerait un envoi texte légitime.
      this.#discardRecording();
      if (submit) this.formTarget.requestSubmit();
      return;
    }

    this.#attachRecording(blob);
    // L'état « prêt » est posé dans les DEUX cas, y compris juste avant un
    // envoi : si le POST est refusé (422 média), la barre doit déjà être figée
    // et la pastille éteinte — sinon elle continuerait de pulser au-dessus d'un
    // message d'erreur, comme si l'on enregistrait encore.
    this.#enterReadyUi();

    if (submit) this.formTarget.requestSubmit();
  }

  // Le Blob devient un File nommé, posé dans l'input caché via `DataTransfer` :
  // c'est la seule façon d'écrire une FileList, et c'est l'idiome déjà utilisé
  // par `photo_picker_controller.js#_syncInput`.
  #attachRecording(blob) {
    if (!this.hasAudioInputTarget) return;

    const extension = this.recorderMimeType.includes('ogg') ? 'ogg' : 'webm';
    const file = new File([blob], `vocal.${extension}`, { type: blob.type });
    const transfer = new DataTransfer();
    transfer.items.add(file);
    this.audioInputTarget.files = transfer.files;
  }

  #discardRecording() {
    this.chunks = [];
    this.recorder = null;
    this.cancelled = false;
    this.submitAfterStop = false;
    if (this.hasAudioInputTarget) this.audioInputTarget.value = '';
    this.#exitRecordingUi();
  }

  // --------------------------------------------------------------- états ---

  #enterRecordingUi() {
    this.#applyTokens(this.#target('photoButton'), { add: 'disabledClass' });
    if (this.hasPhotoButtonTarget) this.photoButtonTarget.disabled = true;

    this.#applyTokens(this.#target('micButton'), { add: 'recordingClass' });
    this.#setMicLabel('recordingLabel');

    if (this.hasInputWrapperTarget) this.inputWrapperTarget.classList.add('hidden');
    if (this.hasCancelWrapperTarget) this.cancelWrapperTarget.classList.remove('hidden');

    // ORDRE LOAD-BEARING (D9, même correctif que `#revealMediaError`) : on
    // RÉVÈLE la région AVANT d'y écrire. Un nœud `display:none` est hors de
    // l'arbre d'accessibilité — un texte déjà présent au dévoilement n'est
    // annoncé par AUCUN lecteur, même sous `role="status"`.
    if (this.hasRecordingBarTarget) this.recordingBarTarget.classList.remove('hidden');
    this.#writeRecordingLabel('recordingText');

    this.elapsed = 0;
    this.#renderTimer();
    this.tickTimer = setInterval(() => this.#tick(), 1000);
  }

  // Plafond atteint : la barre RESTE affichée, figée, pastille éteinte, et
  // l'enregistrement attend « Envoyer » ou « Annuler ». JAMAIS d'envoi auto.
  #enterReadyUi() {
    this.#clearTick();
    this.#applyTokens(this.#target('recordingDot'), { remove: 'activeClass', add: 'idleClass' });
    this.#writeRecordingLabel('readyText');
    this.#setMicLabel('idleLabel');
    this.#applyTokens(this.#target('micButton'), { remove: 'recordingClass' });
  }

  #exitRecordingUi() {
    this.#clearTick();

    if (this.hasRecordingBarTarget) this.recordingBarTarget.classList.add('hidden');
    if (this.hasRecordingLabelTarget) this.recordingLabelTarget.textContent = '';
    this.#applyTokens(this.#target('recordingDot'), { remove: 'idleClass', add: 'activeClass' });

    this.elapsed = 0;
    this.#renderTimer();

    if (this.hasCancelWrapperTarget) this.cancelWrapperTarget.classList.add('hidden');
    if (this.hasInputWrapperTarget) this.inputWrapperTarget.classList.remove('hidden');

    this.#applyTokens(this.#target('micButton'), { remove: 'recordingClass' });
    this.#setMicLabel('idleLabel');

    this.#applyTokens(this.#target('photoButton'), { remove: 'disabledClass' });
    if (this.hasPhotoButtonTarget) this.photoButtonTarget.disabled = false;
  }

  // Décision (5) : `navigator.mediaDevices` est `undefined` hors contexte
  // sécurisé (la QA LAN se fait en http) — un `getUserMedia` y lèverait un
  // TypeError opaque. On le dit AU CHARGEMENT et on grise le micro de façon
  // MARQUÉE (pattern P0a), plutôt que de laisser cliquer un bouton mort.
  #markMicUnavailableIfNeeded() {
    if (this.#micSupported()) return;

    if (this.hasMicButtonTarget) {
      this.micButtonTarget.disabled = true;
      this.#applyTokens(this.micButtonTarget, { add: 'disabledClass' });
      this.#setMicLabel('unavailableLabel');
    }

    this.#revealMediaError(this.#alertCopy('unsupportedMessage'));
  }

  #micSupported() {
    return (
      typeof MediaRecorder !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      typeof navigator.mediaDevices?.getUserMedia === 'function'
    );
  }

  // --------------------------------------------------------------- timer ---

  #tick() {
    this.elapsed += 1;
    this.#renderTimer();

    // Décision (6) : arrêt AUTOMATIQUE, état « prêt à envoyer ». Jamais d'envoi.
    if (this.elapsed >= this.maxSecondsValue) this.#stopRecording({ submit: false });
  }

  #renderTimer() {
    if (!this.hasRecordingTimerTarget) return;

    const nearCap = this.elapsed >= this.warnSecondsValue;
    // Au-delà de 4:30 le plafond est RAPPELÉ dans le chrono (« 4:31 / 5:00 ») :
    // une couleur seule ne dit pas de combien de temps il reste.
    this.recordingTimerTarget.textContent = nearCap
      ? `${this.#clock(this.elapsed)} / ${this.#clock(this.maxSecondsValue)}`
      : this.#clock(this.elapsed);

    this.#applyTokens(this.recordingTimerTarget, {
      remove: nearCap ? 'idleClass' : 'warnClass',
      add: nearCap ? 'warnClass' : 'idleClass',
    });
  }

  #clock(seconds) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
  }

  #clearTick() {
    if (!this.tickTimer) return;

    clearInterval(this.tickTimer);
    this.tickTimer = null;
  }

  #clearMicPermissionTimeout() {
    if (!this.micPermissionTimeout) return;

    clearTimeout(this.micPermissionTimeout);
    this.micPermissionTimeout = null;
  }

  // ------------------------------------------------------------- plomberie --

  #resetRecorderState() {
    this.recorder = null;
    this.recorderMimeType = PREFERRED_MIME;
    this.stream = null;
    this.chunks = [];
    this.elapsed = 0;
    this.tickTimer = null;
    this.cancelled = false;
    this.submitAfterStop = false;
    this.micRequestToken = null;
    this.micPermissionTimeout = null;
  }

  #isRecording() {
    return this.recorder?.state === 'recording';
  }

  #stopRecorderNow() {
    if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
    this.recorder = null;
  }

  // Sans ça le voyant « micro actif » de l'onglet reste allumé après l'envoi.
  #releaseStream() {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }

  // Les jeux de classes vivent en data-attrs sur les nœuds de l'ERB (D14) : le
  // JS ne connaît que des NOMS DE CLÉ, jamais un token Tailwind ni une couleur.
  #applyTokens(element, { add, remove } = {}) {
    if (!element) return;

    const tokens = (key) => (element.dataset[key] || '').split(' ').filter(Boolean);
    const removed = remove ? tokens(remove) : [];
    const added = add ? tokens(add) : [];
    if (removed.length > 0) element.classList.remove(...removed);
    if (added.length > 0) element.classList.add(...added);
  }

  #target(name) {
    return this[`has${name[0].toUpperCase()}${name.slice(1)}Target`] ? this[`${name}Target`] : null;
  }

  #setMicLabel(key) {
    if (!this.hasMicButtonTarget) return;

    const label = this.micButtonTarget.dataset[key];
    if (label) this.micButtonTarget.setAttribute('aria-label', label);
  }

  #writeRecordingLabel(key) {
    if (!this.hasRecordingLabelTarget) return;

    this.recordingLabelTarget.textContent = this.recordingLabelTarget.dataset[key] || '';
  }

  #alertCopy(key) {
    return this.hasMediaAlertTarget ? this.mediaAlertTarget.dataset[key] : '';
  }

  // --------------------------------------------------------------- envoi ---

  afterSubmit(event) {
    // Fix round 1 (revue T5+T9) : point de ré-activation choisi pour les
    // quick-actions — `turbo:submit-end` est le SEUL événement commun aux
    // deux issues (succès `no_content` ET refus 422 média), et il tombe AVANT
    // le `return` anticipé de la branche échec ci-dessous, donc la rangée ne
    // reste JAMAIS morte après un refus serveur. Pas de garde `success` ici :
    // ré-activer des boutons déjà actifs (tout envoi qui n'est pas passé par
    // `quickAction`) est un no-op sans effet de bord.
    this.#reenableQuickActions();

    if (!event.detail.success) {
      this.#showMediaError(event);
      return;
    }

    this.#hideMediaError();
    this.inputTarget.value = '';
    // Le serveur répond `no_content` : rien ne re-rend le compositeur, donc la
    // pile de fichiers stagés survivrait à l'envoi si on ne la vidait pas ici.
    if (this.hasPhotoPickerOutlet) this.photoPickerOutlet.clear();
    // Idem pour le vocal : l'input caché garderait son File et le MÊME
    // enregistrement repartirait avec le message suivant.
    this.#discardRecording();
    this.inputTarget.focus();
    // 4e (suites #756) — un clic servi SANS tour a déjà sa bulle à l'écran : elle
    // part avant la réponse HTTP, donc avant cet événement, et aucune bulle
    // ultérieure n'éteindrait l'indicateur. Le serveur le dit par la réponse de
    // CE submit (`Dev::Agent::MessagesController::REPLIED_HEADER`), jamais par
    // l'observation du DOM : une bulle tardive d'un tour précédent ne doit pas
    // masquer une vraie attente.
    if (event.detail.fetchResponse?.response?.headers?.get('X-Agent-Replied') === 'sync') return;
    // Le bouton « Envoyer » reste ACTIF : les envois successifs sont collectés
    // par le débounce du canal.
    this.#showTyping();
  }

  // Le corps du 422 média est le message FR du serveur (source unique des
  // bornes). Repli sur la copie portée en data-attr quand le refus est muet
  // (uuid vide, corps ET fichiers absents) — jamais une phrase figée en JS.
  #showMediaError(event) {
    if (!this.hasMediaAlertTarget) return;

    const fallback = this.#alertCopy('defaultMessage') || '';
    const responseText = event.detail?.fetchResponse?.responseText;

    if (responseText?.then) {
      responseText.then((text) => this.#revealMediaError(text.trim() || fallback));
    } else {
      this.#revealMediaError(fallback);
    }
  }

  // ORDRE LOAD-BEARING : on RÉVÈLE avant d'écrire. Un nœud `display:none` est
  // hors de l'arbre d'accessibilité, donc une mutation de texte qui y tombe
  // n'est annoncée par AUCUN lecteur d'écran, même sous `aria-live` — c'est
  // seulement au retrait de `hidden` que la région devient observable, et elle
  // l'est alors déjà porteuse de l'ancien message. Même correctif que
  // `subcontract_agreed_amount_controller.js:184`.
  #revealMediaError(text) {
    if (!this.hasMediaAlertTarget) return;

    this.mediaAlertTarget.classList.remove('hidden');
    if (this.hasMediaAlertMessageTarget) this.mediaAlertMessageTarget.textContent = text || '';
  }

  #hideMediaError() {
    if (this.hasMediaAlertTarget) this.mediaAlertTarget.classList.add('hidden');
  }

  // -------------------------------------------------------------- typing ---

  #showTyping() {
    if (!this.hasTypingTarget) return;

    // ORDRE LOAD-BEARING : on écrit le libellé par DÉFAUT avant de révéler, pour
    // qu'un tour qui suit un tour lent ne réaffiche pas « c'est plus long que
    // d'habitude » sur sa première seconde.
    this.#setTypingLabel('typingLabel');
    this.typingTarget.classList.remove('hidden');
    this.#scrollMessagesToBottom();
    this.#clearTypingTimeout();
    this.typingTimeout = setTimeout(() => this.#degradeTyping(), TYPING_SLOW_AFTER_MS);
  }

  // Le tour dépasse la normale mesurée : on le DIT, on ne se tait pas.
  #degradeTyping() {
    this.#clearTypingTimeout();
    this.#setTypingLabel('typingSlowLabel');
    this.typingTimeout = setTimeout(() => this.#hideTyping(), TYPING_HARD_STOP_MS);
  }

  // La copie FR vit dans le `data-*` du gabarit, JAMAIS ici : ni `ui-lint-erb.sh`
  // ni `lint-terminology.sh` ne lisent les `.js`, donc une phrase figée en JS
  // échapperait aux deux linters de copie du dépôt.
  #setTypingLabel(key) {
    if (!this.hasTypingLabelTarget || !this.hasTypingTarget) return;

    const text = this.typingTarget.dataset[key];
    if (text) this.typingLabelTarget.textContent = text;
  }

  #hideTyping() {
    this.#clearTypingTimeout();
    if (this.hasTypingTarget) this.typingTarget.classList.add('hidden');
  }

  // ⚠️ SEUL UN `assistant` ÉTEINT L'INDICATEUR — JAMAIS UN `system`.
  //
  // Le discriminant a été `!== 'user'`, et il DÉFAISAIT la dégradation à 90 s
  // sur le seul chemin pour lequel elle a été écrite. Depuis que tout vocal
  // reçoit son accusé (`TurnJob::TRANSCRIPTION_ACK`, poussé par
  // `Channels::Dev#send_system` en bulle `role="system"`), une dictée voyait :
  // indicateur à t=0, accusé à t≈3 s, indicateur ÉTEINT — puis plus rien
  // pendant tout le tour LLM. Or un bandeau système (accusé de transcription,
  // ouverture ou clôture de séance) annonce un FAIT, il n'est pas « la
  // réponse » : seule une bulle assistant termine l'attente.
  //
  // Les trois rôles rendus par `dev/agent/console/_message.html.erb` sont
  // `user` / `assistant` / `system` : la forme POSITIVE est donc aussi celle
  // qui ne se trompera pas sur un quatrième rôle futur — un rôle inconnu
  // n'éteint rien, ce qui est le défaut sûr (l'indicateur a sa propre
  // extinction dure).
  #hideTypingOnReply(mutations) {
    const replyArrived = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          node.getAttribute('data-agent-message-role') === 'assistant'
      )
    );

    if (replyArrived) this.#hideTyping();
  }

  #clearTypingTimeout() {
    if (!this.typingTimeout) return;

    clearTimeout(this.typingTimeout);
    this.typingTimeout = null;
  }

  #scrollMessagesToBottom() {
    if (!this.hasMessagesTarget) return;

    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
    });
  }
}
